



















































































































import { mapGetters } from 'vuex'

import Generator from '@/util/password/Generator'
import { Vue, Component, Prop } from 'vue-property-decorator'
import { Getter, State, Action, Mutation } from 'vuex-class'
import { RootState } from '@/store/types'
import { EditorEntry, Field, OnChangeHandler } from '@/store/editor/types'
import { Maybe, Nullable } from '@/types/general'
import StrengthTester, { StrengthScoreResults } from '@/util/password/StrengthTester'
import { isElectron, constrain } from '@/util/Helpers'
import { ENTRY_UPDATE, EDITOR_SET_FIELD_DATA, EDITOR_RESTORE_FIELD } from '@/store/editor/constants'
import { DiffedRevision } from '@/store/revision/types'
import VInput from './VInput.vue'

/**
 * Note: DO NOT USE FOR ACTUAL PASSWORDS
 * The passwords in this component are fake and not
 * to be used for actual password fields
 */
@Component({ name: 'v-sync-input' })
export default class VSyncInput extends Vue {
    /**
     * Whether to hide the text shown in the field
     *
     * Only applicable for password fields.
     */
    visible: boolean = false

    /**
     * Whether the cancel button is hovered over
     */
    cancelHovered: boolean = false

    /**
     * Hacky way to force update the input when set via state.
     */
    updater: number = 0

    /**
     * Whether the field is focused
     */
    focus: boolean = false

    /**
     * Internal value in the v-input
     */
    internalValue: string|null = null

    /**
     * Name of field
     */
    @Prop({ type: String, required: true })
    fieldKey!: string

    /**
     * Name of field
     */
    @Prop({ type: Boolean, default: true })
    mono!: boolean

    /**
     * Whether the current user can modify the entry
     */
    @Getter
    canModify!: boolean

    /**
     * Commit the change to the entry
     */
    @Action(ENTRY_UPDATE)
    updateEntry!: () => Promise<void>

    /**
     * Set the value for a field
     */
    @Mutation(EDITOR_SET_FIELD_DATA)
    setFieldData!: ({ key, value }: { key: string, value: string }) => void

    /**
     * Restore a field to it's initial value before being edited
     */
    @Mutation(EDITOR_RESTORE_FIELD)
    restoreField!: (key: string) => void

    /**
     * Current editor entry
     */
    @State((state: RootState) => state.editor.entry)
    entry!: EditorEntry

    /**
     * The revision currently being viewed (null if no revision)
     */
    @State((state: RootState) => state.revision.viewing)
    revision!: Nullable<DiffedRevision>

    /**
     * Whether the entry already exists
     */
    get existing(): boolean {
        return this.entry.existing
    }

    /**
     * Field stencil
     */
    get field(): Field {
        return this.entry.fields[this.fieldKey]
    }

    /**
     * Field name
     */
    get name(): string {
        return this.field.name
    }

    /**
     * Field value
     */
    get value(): string {
        return this.field.value || ''
    }

    // prevent issues in vue-clipboards reading null
    get copyValue(): string {
        return this.value || ''
    }

    /**
     * Error status of the field
     */
    get error(): Maybe<string> {
        return this.field.error
    }

    /**
     * State status of the field
     */
    get status(): Maybe<string> {
        return this.field.status
    }

    /**
     * Tooltip to display in the label of the input
     */
    get tooltip(): Maybe<string> {
        return this.field.input && this.field.input.tooltip
    }

    /**
     * Input field type
     */
    get actualType(): Maybe<string> {
        return this.field.input && this.field.input.type
    }

    /**
     * Height (in lines) of the input
     */
    get height(): number {
        const minHeight = (this.field.input && this.field.input.minHeight) || 1
        if (minHeight > 1) {
            // get dynamic height, only if field text area
            const dynamicHeight = this.value.split('\n').length
            const maxHeight = this.field.input!.maxHeight || 1
            return constrain(dynamicHeight, minHeight, maxHeight)
        }
        return 1
    }

    /**
     * HTML element type
     *
     * Switches to 'textarea' if a height is set, otherwise will
     * be 'text'.
     */
    get element(): 'textarea' | 'text' {
        return this.height > 1 ? 'textarea' : 'text'
    }

    /**
     * Whether the field is a password field
     */
    get isPassword(): boolean {
        return this.actualType === 'password'
    }

    /**
     * Whether the stencil field has an external link
     */
    get link(): boolean {
        return (!!this.field.input && !!this.field.input.link)
    }

    /**
     * URI to link to
     *
     * Supports any URI schema, works well with SSH as well as HTTP.
     */
    get uri(): Maybe<string> {
        if (!this.link) return ''
        if (this.field.input && this.field.input.uriSchema) {
            const username = this.entry.fields.username
                ? this.entry.fields.username.value
                : undefined
            if (username) {
                return `${this.field.input.uriSchema}://${username}@${this.value}`
            }
            return `${this.field.input.uriSchema}://${username}@${this.value}`
        }
        if (!this.value.includes('http://') && !this.value.includes('https://')) {
            return `http://${this.value}`
        }
        return this.value
    }

    /**
     * Whether the field is copyable
     */
    get copy(): boolean {
        return !(this.field.input && this.field.input.copy === false)
    }

    /**
     * Whether the input should be displayed full width
     */
    get fullWidth(): boolean {
        return !!this.field.input && !!this.field.input.fullWidth
    }

    /**
     * Icon to display the visibility of the field
     *
     * Only applicable to passwords.
     */
    get visibilityIcon(): string {
        return this.visible ? 'eye-slash' : 'eye'
    }

    /**
     * Stencil field's onChange event
     */
    get onChange(): Maybe<OnChangeHandler> {
        return this.field.input ? this.field.input.onChange : undefined
    }

    /**
     * The number of buttons shown on the input
     *
     * Used to calculate padding on the input itself to give space for
     * the buttons.
     */
    get buttonCount(): number {
        let count = 0
        if (this.existing && this.status !== 'unchanged') count += 1
        if (this.isPassword) count += 1
        if (this.field.input && this.field.input.generate && this.canModify) count += 1
        if (this.existing && this.link) count += 1
        if (this.existing && this.copy && this.value !== '') count += 1
        return count
    }

    /**
     * Whether the input is a diff view
     */
    get isDiff(): boolean {
        if (this.revision) {
            return this.diffBefore !== undefined
        }
        return false
    }

    get isInitialDiff(): boolean {
        return !!this.revision && this.revision.id === 0
    }

    get diffBefore(): Maybe<string> {
        if (this.revision!.diffs[this.fieldKey]) {
            return this.revision!.diffs[this.fieldKey][0]
        }
        return undefined
    }

    get diffAfter(): Maybe<string> {
        if (this.revision!.content[this.fieldKey]) {
            return this.revision!.content[this.fieldKey]
        }
        return undefined
    }

    get changed(): boolean {
        return this.value !== this.internalValue
    }

    mounted(): void {
        this.internalValue = this.value
    }

    onInternalInput(value: string): void {
        this.internalValue = value

        if (!this.existing) {
            // instantly save for new entries
            this.update(value)
        }
    }

    /**
     * Open the URL
     *
     * For electron, attempts to open in the user's default browser.
     * Otherwise will open a new tab/window for web
     */
    followLink(): void {
        if (isElectron()) {
            require('electron').shell.openExternal(this.uri!)
        } else {
            window.open(this.uri!, '_blank')
        }
    }

    update(value: string): void {
        const old = this.value
        this.setFieldData({ key: this.fieldKey, value })
        if (this.onChange && this.onChange(old, value, this) === false) {
            this.$nextTick(() => {
                this.setFieldData({ key: this.fieldKey, value: old })
                this.updater += 1
            })
        }
    }

    /**
     * Set whether the input is focused
     */
    setFocus(focus: boolean): void {
        this.focus = focus
    }

    /**
     * Update field on loss of focus
     */
    blur(): void {
        this.setFocus(false)
        if (this.existing && this.status === 'changed') {
            // if no timeout this would happen before 'restore value'
            // is fired when canceling edit
            setTimeout(this.updateEntry, 500)
        }
    }

    /**
     * Generate a password in the field
     */
    generatePassword(): void {
        const password = Generator.generate();
        (this.$refs.input as VInput).setInternalValue(password)
    }
}
